import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// FD-1235: the guided tour. The server lists the steps (title, blurb, url with ?tour=n, path);
// this controller remembers which step the person is on in sessionStorage, so clicking around
// a page keeps the panel, and every page load re-renders it from that. Start marks the tour
// seen (so the strip button goes away) and opens step one. Next and Back visit the next step's
// url; End clears the place and hides the panel. A ?tour=n in the URL sets the step and is
// stripped from the address bar so a reload or a copied link doesn't restart from it.
const KEY = "fd:product-tour"

// FD-1251: whether a tour is under way in this tab. The first-visit overlay
// (feature_tour_controller.js) asks, and stays shut while one is. A saved step, or a ?tour=n
// that this page's product-tour#connect has not saved and stripped yet.
export function productTourRunning() {
  return savedStep() > 0 || new URLSearchParams(window.location.search).has("tour")
}

function savedStep() {
  try { return parseInt(sessionStorage.getItem(KEY) || "0", 10) || 0 } catch (_e) { return 0 }
}

export default class extends Controller {
  static targets = ["panel", "progress", "dots", "title", "blurb", "away", "back", "next"]
  static values = { steps: Array, url: String, feature: String }

  connect() {
    const fromUrl = this.stepFromUrl()
    if (fromUrl) this.save(fromUrl)
    this.render()
  }

  // The strip's rocket lives outside this controller's element; see shared/_product_tour.
  startFromStrip(event) {
    if (event.target.closest("[data-product-tour-button]")) this.start(event)
  }

  start(event) {
    event?.preventDefault()
    this.markSeen()
    document.querySelector("[data-product-tour-button]")?.setAttribute("hidden", "")
    this.go(1)
  }

  next() { this.current >= this.stepsValue.length ? this.end() : this.go(this.current + 1) }
  back() { if (this.current > 1) this.go(this.current - 1) }
  return(event) { event.preventDefault(); this.go(this.current) }

  end() {
    try { sessionStorage.removeItem(KEY) } catch (_e) {}
    this.panelTarget.hidden = true
  }

  go(step) {
    this.save(step)
    const target = this.stepsValue[step - 1]
    if (target) Turbo.visit(target.url)
  }

  render() {
    const step = this.current
    const data = this.stepsValue[step - 1]
    if (!step || !data) { this.panelTarget.hidden = true; return }
    const total = this.stepsValue.length
    this.progressTarget.textContent = `Step ${step} of ${total}`
    this.titleTarget.textContent = data.title
    this.blurbTarget.textContent = data.blurb
    this.dotsTarget.replaceChildren(...this.stepsValue.map((_, i) => {
      const dot = document.createElement("span")
      dot.className = "inline-block size-1.5 rounded-full " + (i + 1 <= step ? "bg-brand" : "bg-outline")
      return dot
    }))
    this.backTarget.disabled = step <= 1
    this.nextTarget.textContent = step >= total ? "Finish" : "Next"
    this.awayTarget.hidden = window.location.pathname === data.path
    this.panelTarget.hidden = false
  }

  get current() { return savedStep() }

  save(step) {
    try { sessionStorage.setItem(KEY, String(step)) } catch (_e) {}
  }

  // ?tour=n sets the step, then leaves the address bar; out-of-range values are ignored.
  stepFromUrl() {
    const url = new URL(window.location.href)
    const raw = url.searchParams.get("tour")
    if (raw === null) return 0
    url.searchParams.delete("tour")
    window.history.replaceState(window.history.state, "", url.toString())
    const n = parseInt(raw, 10)
    return n >= 1 && n <= this.stepsValue.length ? n : 0
  }

  // Fire and forget: the strip button is already hidden here, and the server hides it on
  // the next page once the row exists. A failure just means the button shows once more.
  markSeen() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content || ""
    fetch(this.urlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": token },
      body: JSON.stringify({ feature: this.featureValue }),
      credentials: "same-origin"
    }).catch(() => {})
  }
}
